// /sends — paged list of past newsletter sends with engagement counts.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { newsletterApi } from '../lib/api';
import type { NewsletterSend } from '@shared/types';

const PAGE_SIZE = 50;

const STATUS_COLORS: Record<NewsletterSend['status'], string> = {
  pending: 'bg-slate-100 text-slate-700',
  sending: 'bg-amber-100 text-amber-800',
  sent: 'bg-green-100 text-green-800',
  partial: 'bg-amber-100 text-amber-800',
  failed: 'bg-red-100 text-red-800',
};

export default function SendsListPage() {
  const [sends, setSends] = useState<NewsletterSend[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setErr(null);
    newsletterApi
      .listSends({ page, page_size: PAGE_SIZE })
      .then((res) => {
        setSends(res.sends);
        setTotal(res.total);
      })
      .catch((e) => setErr(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      <div className="max-w-5xl mx-auto py-6 px-4 sm:py-10 sm:px-6">
        <header className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display text-3xl text-slate-900">Sends</h1>
            <p className="text-sm text-slate-500 mt-1">
              {loading ? 'Loading…' : `${total} newsletter ${total === 1 ? 'send' : 'sends'}`}
            </p>
          </div>
        </header>

        {err && <p className="text-sm text-red-600 mb-4">{err}</p>}

        {loading && sends.length === 0 ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : sends.length === 0 ? (
          <div className="border-2 border-dashed border-slate-200 rounded-lg p-12 text-center text-sm text-slate-500">
            No sends yet. Send your first newsletter from a draft.
          </div>
        ) : (
          <>
            {/* Mobile cards */}
            <ul className="sm:hidden bg-white border border-slate-200 rounded-md divide-y divide-slate-100">
              {sends.map((s) => (
                <li key={s.id}>
                  <Link to={`/sends/${s.id}`} className="block px-4 py-3 active:bg-slate-50">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-sm font-medium text-slate-900 truncate">
                        {s.subject || <span className="text-slate-400">(no subject)</span>}
                      </span>
                      <span className={`text-[11px] inline-flex items-center px-2 py-0.5 rounded flex-shrink-0 ${STATUS_COLORS[s.status]}`}>
                        {s.status}
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">
                      {s.sent_at
                        ? new Date(s.sent_at).toLocaleString()
                        : new Date(s.created_at).toLocaleString()}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
                      <span>To {s.recipient_count}</span>
                      <span>·</span>
                      <span>Open {pct(s.opened_count, s.recipient_count)}</span>
                      <span>·</span>
                      <span>Click {pct(s.clicked_count, s.recipient_count)}</span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>

            {/* Desktop table */}
            <div className="hidden sm:block bg-white border border-slate-200 rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Subject</th>
                    <th className="text-left px-4 py-2 font-medium">Sent</th>
                    <th className="text-left px-4 py-2 font-medium">Status</th>
                    <th className="text-right px-4 py-2 font-medium">To</th>
                    <th className="text-right px-4 py-2 font-medium">Delivered</th>
                    <th className="text-right px-4 py-2 font-medium">Open</th>
                    <th className="text-right px-4 py-2 font-medium">Click</th>
                    <th className="text-right px-4 py-2 font-medium">Bounce</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {sends.map((s) => (
                    <tr key={s.id} className="hover:bg-slate-50">
                      <td className="px-4 py-2 text-slate-900">
                        <Link to={`/sends/${s.id}`} className="hover:underline">
                          {s.subject || <span className="text-slate-400">(no subject)</span>}
                        </Link>
                        {s.sent_by_email && (
                          <div className="text-xs text-slate-500">by {s.sent_by_email}</div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-slate-500 text-xs">
                        {s.sent_at
                          ? new Date(s.sent_at).toLocaleString()
                          : new Date(s.created_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-2 text-xs">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded ${STATUS_COLORS[s.status]}`}>
                          {s.status}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right text-slate-900">{s.recipient_count}</td>
                      <td className="px-4 py-2 text-right text-slate-700">{pct(s.delivered_count, s.recipient_count)}</td>
                      <td className="px-4 py-2 text-right text-slate-700">{pct(s.opened_count, s.recipient_count)}</td>
                      <td className="px-4 py-2 text-right text-slate-700">{pct(s.clicked_count, s.recipient_count)}</td>
                      <td className="px-4 py-2 text-right text-slate-700">{pct(s.bounced_count, s.recipient_count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-end gap-2 mt-4 text-xs text-slate-600">
              <span>Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
                className="btn-ghost p-2 sm:p-1 disabled:opacity-40"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
                className="btn-ghost p-2 sm:p-1 disabled:opacity-40"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function pct(n: number, total: number): string {
  if (total <= 0) return '—';
  if (n === 0) return '0';
  return `${n} (${Math.round((n / total) * 100)}%)`;
}
